import { useState } from "react";
import { useNavigate } from "react-router";
import { CanAccess, useNotify } from "ra-core";
import { AlertList, useThermoAlerts } from "../thermo/thermo";
import { engineReady, engineShort, useAiSettings, useEngines } from "../ai/ai";
import { CompanyAiDialog } from "../ai/CompanyAiDialog";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  Bell,
  Check,
  Menu,
  Monitor,
  Moon,
  Plus,
  Search,
  Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UserMenu } from "@/components/admin/user-menu";
import { RefreshButton } from "@/components/admin/refresh-button";
import { useTheme } from "@/components/admin/use-theme";
import { useIsMobile } from "@/hooks/use-mobile";
import { ContactCreateSheet } from "../../contacts/ContactCreateSheet";
import { NoteCreateSheet } from "../../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../../tasks/TaskCreateSheet";
import {
  ChangelogMenuItem,
  ImportFromJsonMenuItem,
  ProfileMenu,
  SettingsMenu,
  UsersMenu,
} from "../../layout/Header";

const THEMES = [
  { id: "light", label: "Claro", Icon: Sun },
  { id: "dark", label: "Oscuro", Icon: Moon },
  { id: "system", label: "Automático (como tu equipo)", Icon: Monitor },
] as const;

const directorySearch = (q: string) =>
  `/directory?filter=${encodeURIComponent(
    JSON.stringify({ "nombre_comercial@ilike": q }),
  )}`;

/** Barra global estilo Jira: buscar, crear, tema, motor de IA, alertas, cuenta. */
export const GlobalBar = ({ onMenu }: { onMenu?: () => void }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const notify = useNotify();
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState<"contact" | "note" | "task" | null>(null);
  const [aiCompany, setAiCompany] = useState(false);
  const { data: ai } = useAiSettings();
  const { data: engines } = useEngines();
  const engineWhy = ai ? engineReady(ai.ai_engine, engines) : null;
  const { theme, setTheme } = useTheme();
  const ThemeIcon = THEMES.find((t) => t.id === theme)?.Icon ?? Monitor;

  const create = [
    {
      label: "Empresa (alta manual)",
      hint: "Se agrega a Empresas",
      run: () => navigate("/companies/create"),
      hide: isMobile, // en celular llega con el Bloque D
    },
    {
      label: "Empresa con IA",
      hint: "Pega un nombre o sitio web y la IA llena la ficha",
      run: () => setAiCompany(true),
    },
    {
      label: "Contacto",
      hint: "Dueño, compras, RR.HH.…",
      run: () => setSheet("contact"),
    },
    {
      label: "Oportunidad",
      hint: "Empresa × producto",
      run: () => navigate("/deals/create"),
    },
    {
      label: "Tarea",
      hint: "Llamada, WhatsApp o correo",
      run: () => setSheet("task"),
    },
    {
      label: "Nota",
      hint: "En cualquier contacto",
      run: () => setSheet("note"),
    },
  ];

  return (
    <header className="flex h-13 items-center gap-2 border-b bg-card px-4 md:px-5 shrink-0">
      {isMobile && (
        <Button
          variant="outline"
          size="icon"
          onClick={onMenu}
          aria-label="Abrir menú"
        >
          <Menu />
        </Button>
      )}
      {isMobile ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Buscar en el directorio"
          onClick={() => navigate("/directory")}
        >
          <Search />
        </Button>
      ) : (
        <form
          className="flex h-9 w-full max-w-[520px] items-center gap-2 rounded-lg border bg-card px-2.5"
          onSubmit={async (e) => {
            e.preventDefault();
            const text = q.trim();
            if (!text) return;
            // Clave tipo Jira (STK-3): abre esa oportunidad
            if (/^[a-z]{2,4}-\d+$/i.test(text)) {
              const { data } = await getSupabaseClient()
                .from("deals")
                .select("id")
                .eq("key", text.toUpperCase())
                .maybeSingle();
              if (data) return navigate(`/deals/${data.id}/show`);
              return notify(
                `No hay ninguna oportunidad ${text.toUpperCase()}`,
                {
                  type: "warning",
                },
              );
            }
            navigate(directorySearch(text));
          }}
        >
          <Search className="size-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar empresa o clave (STK-3)…"
            aria-label="Búsqueda global"
            className="w-full bg-transparent text-sm outline-none"
          />
        </form>
      )}
      <span className="flex-1" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={isMobile ? "icon" : "default"} aria-label="Crear">
            <Plus />
            {!isMobile && "Crear"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {create
            .filter((c) => !c.hide)
            .map((c) => (
              <DropdownMenuItem
                key={c.label}
                onSelect={() => c.run()}
                className="flex-col items-start gap-0 py-2"
              >
                <span className="font-semibold">{c.label}</span>
                <span className="text-xs text-muted-foreground">{c.hint}</span>
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Tema claro u oscuro">
            <ThemeIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Tema</DropdownMenuLabel>
          {THEMES.map((t) => (
            <DropdownMenuItem key={t.id} onSelect={() => setTheme(t.id)}>
              <t.Icon />
              {t.label}
              {theme === t.id && <Check className="ml-auto" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Motor de IA activo (se cambia en IA y ajustes) */}
      <Button
        variant="outline"
        className="hidden rounded-full sm:inline-flex"
        onClick={() => navigate("/ajustes#ia")}
        title={
          engineWhy ?? "Motor de IA para investigar (cámbialo en IA y ajustes)"
        }
      >
        <span
          className={`size-2 rounded-full ${engineWhy ? "bg-[var(--critical)]" : "bg-[var(--good)]"}`}
        />
        IA: {engineShort(ai?.ai_engine ?? "auto")}
      </Button>

      <AlertsBell />

      {!isMobile && <RefreshButton />}
      {!isMobile && (
        <UserMenu>
          <ProfileMenu />
          <CanAccess resource="sales" action="list">
            <UsersMenu />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <SettingsMenu />
          </CanAccess>
          <ImportFromJsonMenuItem />
          <ChangelogMenuItem />
        </UserMenu>
      )}

      <ContactCreateSheet
        open={sheet === "contact"}
        onOpenChange={(o) => !o && setSheet(null)}
      />
      <NoteCreateSheet
        open={sheet === "note"}
        onOpenChange={(o) => !o && setSheet(null)}
      />
      <CompanyAiDialog open={aiCompany} onClose={() => setAiCompany(false)} />
      <TaskCreateSheet
        open={sheet === "task"}
        onOpenChange={(o) => !o && setSheet(null)}
      />
    </header>
  );
};

/** Campana: alertas del termómetro con contador. */
const AlertsBell = () => {
  const { data: alerts } = useThermoAlerts();
  const [open, setOpen] = useState(false);
  const n = alerts?.length ?? 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Alertas del termómetro: ${n}`}
          className="relative"
        >
          <Bell />
          {n > 0 && (
            <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--critical)] px-1 text-[10px] font-extrabold text-white">
              {n}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[70vh] w-[min(22rem,calc(100vw-1rem))] overflow-y-auto p-2"
      >
        <p className="px-2 py-1 font-semibold">Alertas del termómetro</p>
        {n ? (
          <AlertList alerts={alerts!} onPick={() => setOpen(false)} />
        ) : (
          <p className="px-2 pb-1 text-sm text-muted-foreground">
            Sin alertas por ahora. Aquí verás qué oportunidades se enfrían,
            bajan o se calientan, y empresas muy calientes que aún no están en
            el CRM.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
